import { parse, format, add } from 'date-fns';
import { interpolate } from '@format/interpolate';
import { costFormatter } from '@format/currency';
import { getPricePropertyName } from '../../products';
import { productTypeReference } from '../../products/constants';
import { interpolations } from './interpolation.js';

export const isDisabled = ({ isValid, isSubmitting, isSubmitted, errors }) =>
  !isValid || isSubmitting || isSubmitted || errors.address !== undefined;

export const getCheckoutBarProps = ({
  pets,
  cart,
  products,
  formState,
  ctaText,
  refInView,
  period = 'week',
}) => {
  const shown = !refInView;
  let price = '';
  let discountedPrice = '';
  const isSubmitDisabled = formState.isSubmitting || formState.isSubmitted;
  const isBackButtonDisabled = formState.isSubmitting || formState.isSubmitted;

  const pricePerPeriodIndex = getPricePropertyName(period);
  const discountedPricePerPeriodIndex = getPricePropertyName(period, {
    discounted: true,
  });

  if (cart) {
    const pricePerPeriod = cart.line_items.reduce((total, currentProduct) => {
      if (currentProduct[pricePerPeriodIndex]) {
        total += Number(currentProduct[pricePerPeriodIndex]);
      }

      return total;
    }, 0);

    const pricePerPeriodDiscount = cart.line_items.reduce(
      (total, currentProduct) => {
        if (currentProduct[discountedPricePerPeriodIndex]) {
          total += Number(currentProduct[discountedPricePerPeriodIndex]);
        }

        return total;
      },
      0,
    );

    price = costFormatter(pricePerPeriod);
    discountedPrice = costFormatter(pricePerPeriodDiscount);
  }

  let headline;
  if (pets.length > 1)
    headline =
      pets.reduce(
        (prev, pet, index) =>
          prev +
          pet.name.toUpperCase() +
          "'S " +
          (index !== pets.length - 1 ? ' & ' : ''),
        '',
      ) + 'STARTER BOXES';
  else
    headline =
      pets[0].name.toUpperCase() +
      "'S " +
      products[0].weeks_of_food +
      '-WEEK STARTER BOX';

  return {
    headline,
    ctaText,
    shown,
    isSubmitDisabled,
    isBackButtonDisabled,
    price,
    discountedPrice,
    period,
    isSubmitting: formState.isSubmitting || formState.isSubmitted,
  };
};

const getAddons = (products, lineItems, subscriptions) => {
  if (!subscriptions) return [];
  return Object.keys(subscriptions).flatMap(petId =>
    subscriptions[petId].addons.map(addon => {
      const lineItem = lineItems.find(
        lineItem => lineItem.sku === addon.sku && lineItem.pet_id === petId,
      );
      const productInfo = products.find(
        product => product.variants[0].sku === addon.sku,
      );

      return {
        discountedPricePerWeek: lineItem.discounted_price_per_week || 0,
        feedingGuidelines: lineItem.feeding_guidelines,
        petId,
        petName: lineItem.pet_name,
        price: lineItem.price,
        pricePerWeek: lineItem.price_per_week,
        quantity: lineItem.quantity,
        recipes: lineItem.recipes,
        sku: lineItem.sku,
        totalDiscount: lineItem.total_discount,
        productName: productInfo.product_name,
        productType: lineItem.product_type,
        discountedPrice: lineItem.discounted_price || 0,
      };
    }),
  );
};

export const getCheckoutInformationProps = ({
  refreshCart,
  isValidating,
  cart,
  deletePet,
  pets,
  address,
  products,
  subscriptionProducts,
  formState,
  orderSummary,
  formattedOfferAmount,
  recurringOrders,
  buttonRef,
  showAddons,
  period,
  currentOfferCode,
  defaultOfferCode,
}) => {
  const onClickRemovePet = async petId => {
    await deletePet(petId);
    refreshCart(petId);
  };
  let plans = [];
  let addons;
  const disabled = formState.isSubmitting || formState.isSubmitted;
  let orderSummaryProps = { ...orderSummary, onClickRemovePet };
  let recurringOrdersProps = { ...recurringOrders };
  let nextDeliveryDate;
  let recurringDeliveryDate;
  if (cart && products) {
    addons = getAddons(products, cart.line_items, cart.subscriptions);
    orderSummaryProps.pricingList = {
      ...orderSummaryProps.pricingList,
      promoCodeInfo: interpolate(orderSummaryProps.pricingList.promoCodeInfo, {
        defaultName: defaultOfferCode,
        discount: formattedOfferAmount,
        offerCode: currentOfferCode,
      }),
      total: Number(cart.total),
      foodSubtotal: cart.total_by_product_type.mealplans_subtotal.toString(),
      addonsSubtotal: cart.total_by_product_type.addons_subtotal.toString(),
      foodDiscountAmount: cart.line_items[0].total_discount,
      foodDiscountText: `${formattedOfferAmount} off`,
      discountText: `${formattedOfferAmount} off`,
      discountAmount: cart.total_discount,
      tax: cart.total_tax !== '0.00' ? cart.total_tax : '',
    };

    const pricePerPeriodIndex = getPricePropertyName(period);
    const discountedPricePerPeriodIndex = getPricePropertyName(period, {
      discounted: true,
    });

    plans = pets.map(({ id, name, breeds, gender }) => {
      const lineItem = cart.line_items.find(
        item => item.pet_id === id && item.product_type in productTypeReference,
      );
      const petId = lineItem.pet_id;
      const product = products.find(
        product => product.variants[0].sku === lineItem.sku,
      );

      return {
        petId,
        petName: name,
        breeds: breeds.map(({ name }) => name),
        frequency: product.weeks_of_food,
        numPacks: Number(product.variants[0].tray_count),
        bagCount: Number(product.variants[0].bag_count),
        mealCount: Number(product.weeks_of_food * 14),
        productType: product.product_type,
        price: lineItem.price,
        priceWithDiscount: String(lineItem.discounted_price),
        pricePerPeriodWithDiscount: lineItem[discountedPricePerPeriodIndex]
          ? String(lineItem[discountedPricePerPeriodIndex])
          : String(lineItem.discounted_price),
        pricePerPeriod: lineItem[pricePerPeriodIndex]
          ? lineItem[pricePerPeriodIndex]
          : lineItem.price,
        recipes: product.variants[0].recipes,
        gender,
        addons: addons.filter(pet => pet.petId === petId),
        portionSize: product.portion_size || '1', //Defaults to full portion if null,
      };
    });
  }

  if (pets && cart && subscriptionProducts) {
    recurringOrdersProps.foodPacksText = pets
      .map(pet => {
        return interpolate(
          recurringOrdersProps.foodPacksText,
          interpolations(pet, cart, subscriptionProducts),
        );
      })
      .join(' ');
  } else {
    recurringOrdersProps.foodPacksText = '';
  }

  if (cart?.delivery_date) {
    nextDeliveryDate = format(
      parse(cart.delivery_date, 'yyyy-MM-dd', new Date()),
      'EE, MMM d',
    );
  }

  if (pets && address) {
    const smallestTrialFrequency = Math.min(
      ...pets.map(({ trial_plan }) => trial_plan?.frequency),
    );
    recurringDeliveryDate =
      !address.isFallback && address.next_delivery_date
        ? format(
            add(parse(address.next_delivery_date, 'yyyy-MM-dd', new Date()), {
              weeks: smallestTrialFrequency,
            }),
            'MM/dd/yyyy',
          )
        : '';
  }

  return {
    plans,
    nextDeliveryDate,
    recurringDeliveryDate,
    orderSummary: orderSummaryProps,
    recurringOrders: recurringOrdersProps,
    isValidating,
    isSubmitting: formState.isSubmitting || formState.isSubmitted,
    disabled,
    buttonRef,
    showAddons,
    period,
  };
};
